// UI tour catalog — the step list is DERIVED from the nav manifest, never
// written down a second time.
//
// NAMING: this is `tour`, not "onboarding". The product already owns an
// onboarding module (entity_onboarding_*, /onboarding + /me/onboarding — an HR
// new-hire process). Two different things called "onboarding" on the same
// screen is a defect in the user's head, so the word is not used here.
//
// WHY DERIVED: menu-config is already the surface that narrows by authority
// (per-item `requiredCapability`, per-section `managementOnly`), and the rail
// hands us the keys it actually rendered. Deriving the tour from that set buys
// permission-awareness for free and makes a second list — which would drift the
// day someone adds a screen — impossible to write.

import { MENU_SECTIONS, menuLinks, orderSectionsByAuthority } from "@/components/menu-config";

/**
 * Bumped when the tour's step set or copy changes enough that a user who
 * already dismissed it should be shown it again. Persisted per user as
 * `entity_employee.ui_tour_seen_version`; the tour opens when the stored value
 * is null or lower than this.
 *
 * 2 — the tour stopped being a 37-step modal slideshow and became a five-step
 * spotlight over the shell. Someone who dismissed version 1 dismissed a
 * different thing, so they see this one once.
 */
export const TOUR_VERSION = 2;

export interface TourStep {
  /** menu item key — the join back to menu-config. */
  readonly key: string;
  readonly href: string;
  /** `tour.page.<key>.title` */
  readonly titleKey: string;
  /** `tour.page.<key>.body` */
  readonly bodyKey: string;
}

/**
 * The i18n segment is the menu key VERBATIM (hyphens kept), unlike
 * `menu.item.*` which hand-maps `me-directory` → `me_directory`. That hand-map
 * is a place two names can disagree; deriving the segment means the only way to
 * miss a translation is to forget the key entirely — which catalog.test.ts
 * fails on.
 */
export function tourTitleKey(menuKey: string): string {
  return `tour.page.${menuKey}.title`;
}

export function tourBodyKey(menuKey: string): string {
  return `tour.page.${menuKey}.body`;
}

/**
 * Build the tour's steps for one caller.
 *
 * @param visibleMenuKeys the item keys the rail actually rendered for this
 * caller (already narrowed by capability + scope tier). Keys not present in
 * MENU_SECTIONS produce no step — a caller cannot be toured through a
 * destination the nav manifest does not have.
 *
 * Order follows the RAIL, not the declaration order of MENU_SECTIONS:
 * sections come back in `orderSectionsByAuthority` order (the same sort the
 * rail applies to the sections a caller can see) so the tour walks the pages
 * top-to-bottom as they appear on screen. Within a section, declaration order
 * is the rail's order and is preserved as-is.
 */
export function buildTourSteps(visibleMenuKeys: readonly string[]): readonly TourStep[] {
  const wanted = new Set(visibleMenuKeys);
  const visibleSections = MENU_SECTIONS.filter((section) =>
    menuLinks(section.items).some((item) => wanted.has(item.key)),
  );

  return orderSectionsByAuthority(visibleSections).flatMap((section) =>
    menuLinks(section.items)
      .filter((item) => wanted.has(item.key))
      .map((item) => ({
        key: item.key,
        href: item.href,
        titleKey: tourTitleKey(item.key),
        bodyKey: tourBodyKey(item.key),
      })),
  );
}

/** Every menu key the catalog can produce a step for — the drift guard's left-hand side. */
export function allTourMenuKeys(): readonly string[] {
  return MENU_SECTIONS.flatMap((section) => menuLinks(section.items).map((item) => item.key));
}
